import { useCallback, useEffect, useState } from 'react';
import type { Map as CarteGL } from 'maplibre-gl';

/**
 * L'ARBRE DES COUCHES — ce qui fait d'une carte un atelier.
 *
 * ⚠️⚠️ LA LISTE EST DÉRIVÉE DE LA CARTE, JAMAIS RECOPIÉE. `carte.tsx` déclare
 * vingt-cinq couches dans son style. Une liste écrite à la main ici serait une
 * SECONDE table des mêmes couches — et elle divergerait à la première qu'on
 * ajoute : la couche neuve n'apparaîtrait pas dans l'arbre, donc personne ne
 * pourrait l'éteindre, et personne ne saurait qu'elle existe. C'est le défaut
 * le plus cher de ce dépôt, payé sur le SDK de Connect puis sur les deux
 * cartes. On lit `getStyle().layers`.
 *
 * ⚠️ UNE COUCHE QUI NE TOMBE DANS AUCUN GROUPE CONNU EST MONTRÉE QUAND MÊME,
 * dans « Autres ». Le réflexe serait de la filtrer : l'arbre resterait propre
 * et la couche deviendrait invisible à son pilote. Un groupe « Autres » qui se
 * remplit est un signal — il dit qu'une famille nouvelle est née et qu'il faut
 * la nommer.
 */

type Groupe = { cle: string; titre: string; prefixes: string[] };

/**
 * ⚠️ L'ORDRE DES GROUPES EST CELUI DE LA CASCADE CADASTRALE, pas l'ordre de
 * dessin. Un agent cherche « la section » en pensant collectivité → section →
 * parcelle ; le lui présenter dans l'ordre où MapLibre empile ses couches
 * (fond d'abord, étiquettes en dernier) l'obligerait à connaître le moteur de
 * rendu pour trouver une case à cocher.
 */
const GROUPES: Groupe[] = [
  { cle: 'decoupage', titre: 'Découpage administratif', prefixes: ['collectivites', 'collectivite-', 'divisions'] },
  { cle: 'sections', titre: 'Sections', prefixes: ['sections', 'section-'] },
  { cle: 'parcelles', titre: 'Parcelles', prefixes: ['parcelles', 'parcelle-'] },
  { cle: 'bati', titre: 'Bâti', prefixes: ['batiments'] },
  { cle: 'voirie', titre: 'Voirie', prefixes: ['rues'] },
  { cle: 'trace', titre: 'Tracé en cours', prefixes: ['trace-'] },
  { cle: 'fond', titre: 'Fond', prefixes: ['fond'] },
];

/** Ce qu'une couche dessine — dit en français, pas en vocabulaire MapLibre. */
const NATURE: Record<string, string> = {
  fill: 'surface', line: 'trait', symbol: 'étiquette',
  circle: 'point', 'fill-extrusion': 'volume', background: 'fond',
};

interface CoucheVue {
  id: string;
  type: string;
  groupe: string;
  visible: boolean;
  /** Le zoom minimal sous lequel la couche n'est PAS servie. */
  minzoom: number | null;
}

export function GestionnaireCouches(
  { carte, zoomCourant }: { carte: CarteGL | null; zoomCourant?: number },
) {
  const [couches, setCouches] = useState<CoucheVue[]>([]);
  const [replie, setReplie] = useState(false);

  const relire = useCallback(() => {
    if (!carte || !carte.isStyleLoaded()) return;
    const style = carte.getStyle();
    const vues: CoucheVue[] = (style.layers ?? []).map((l: any) => {
      const groupe = GROUPES.find((g) => g.prefixes.some((p) => l.id.startsWith(p)))?.cle ?? 'autres';
      return {
        id: l.id,
        type: String(l.type),
        groupe,
        /* ⚠️ `visibility` est ABSENTE tant que personne ne l'a posée, et son
           absence signifie VISIBLE. La lire comme « non visible » éteindrait
           l'arbre entier au premier rendu, sur une carte parfaitement allumée. */
        visible: (l.layout?.visibility ?? 'visible') !== 'none',
        minzoom: typeof l.minzoom === 'number' ? l.minzoom : null,
      };
    });
    setCouches(vues);
  }, [carte]);

  useEffect(() => {
    if (!carte) return;
    if (carte.isStyleLoaded()) relire();
    else carte.once('load', relire);
    /* `styledata` suit les ajouts de l'outil de tracé : la couche `trace-…`
       apparaît dans l'arbre dès qu'elle est posée, sans rechargement. */
    carte.on('styledata', relire);
    return () => { carte.off('styledata', relire); };
  }, [carte, relire]);

  const basculer = useCallback((id: string, visible: boolean) => {
    if (!carte) return;
    carte.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    setCouches((p) => p.map((c) => (c.id === id ? { ...c, visible } : c)));
  }, [carte]);

  const basculerGroupe = useCallback((groupe: string, visible: boolean) => {
    if (!carte) return;
    for (const c of couches.filter((x) => x.groupe === groupe)) {
      carte.setLayoutProperty(c.id, 'visibility', visible ? 'visible' : 'none');
    }
    setCouches((p) => p.map((c) => (c.groupe === groupe ? { ...c, visible } : c)));
  }, [carte, couches]);

  if (!carte || couches.length === 0) return null;

  /* L'ordre : les groupes nommés d'abord, « Autres » en dernier — et il
     n'apparaît que s'il porte quelque chose. */
  const ordre = [...GROUPES.map((g) => g.cle), 'autres'];
  const titres: Record<string, string> = {
    ...Object.fromEntries(GROUPES.map((g) => [g.cle, g.titre])),
    autres: 'Autres',
  };

  return (
    <div className={`couches${replie ? ' replie' : ''}`}>
      <button type="button" className="couches-tete" onClick={() => setReplie(!replie)}
        aria-expanded={!replie}>
        <span className="couches-titre">Couches</span>
        <span className="couches-compte">{couches.filter((c) => c.visible).length}/{couches.length}</span>
        <span className="couches-chevron" aria-hidden="true">{replie ? '▸' : '▾'}</span>
      </button>

      {!replie && (
        <div className="couches-corps">
          {ordre.map((cle) => {
            const dedans = couches.filter((c) => c.groupe === cle);
            if (dedans.length === 0) return null;
            const toutes = dedans.every((c) => c.visible);
            const aucune = dedans.every((c) => !c.visible);

            return (
              <div key={cle} className="couches-groupe">
                <label className="couches-groupe-tete">
                  <input
                    type="checkbox"
                    checked={toutes}
                    /* ⚠️ L'ÉTAT INDÉTERMINÉ EXISTE ET IL SE VOIT. Un groupe à
                       moitié allumé qui s'affiche « décoché » ferait croire
                       qu'il est éteint, et un clic l'allumerait entièrement en
                       écrasant un réglage fin que l'agent venait de poser. */
                    ref={(el) => { if (el) el.indeterminate = !toutes && !aucune; }}
                    onChange={(ev) => basculerGroupe(cle, ev.target.checked)}
                  />
                  <span>{titres[cle]}</span>
                  <span className="couches-groupe-compte">{dedans.filter((c) => c.visible).length}/{dedans.length}</span>
                </label>

                <ul className="couches-liste">
                  {dedans.map((c) => {
                    /* ⚠️ UNE COUCHE ÉTEINTE PAR LE ZOOM N'EST PAS UNE COUCHE
                       ÉTEINTE PAR L'AGENT. Sans ce repère, cocher une case et
                       ne rien voir apparaître se lit « la case ne marche pas ».
                       On dit le seuil, et à partir de quand elle sera servie. */
                    const horsZoom = c.minzoom !== null && zoomCourant !== undefined
                      && zoomCourant < c.minzoom;
                    return (
                      <li key={c.id} className={horsZoom ? 'hors-zoom' : undefined}>
                        <label>
                          <input type="checkbox" checked={c.visible}
                            onChange={(ev) => basculer(c.id, ev.target.checked)} />
                          <span className="couches-nom">{c.id}</span>
                          <span className="couches-nature">{NATURE[c.type] ?? c.type}</span>
                        </label>
                        {horsZoom && (
                          <em className="couches-note">servie à partir du zoom {c.minzoom}</em>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
